import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Scroll-reveal for public marketing pages (extracted from HomePage at #3125,
 * its second consumer — the front door's own comment named this the moment to
 * extract). Fly-in from a side, or a gentle rise, as it scrolls into view.
 *
 * Under reduced-motion it renders visible from first paint — no hidden state,
 * so nothing flashes. The parent section must clip horizontal overflow
 * (`overflow-x-clip`) so a from-the-side start cannot add a scrollbar on narrow
 * screens. Behaviour is byte-identical to the original front-door component;
 * `HomePage.landing.test.tsx` still guards the page that first shipped it.
 */
export default function Reveal({
  children,
  className = '',
  delay = 0,
  from = 'up',
  rootMargin = '0px 0px -8% 0px',
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  from?: 'up' | 'left' | 'right';
  /** How far into the viewport the element must be before it reveals. A larger
   *  negative bottom (e.g. '0px 0px -30% 0px') delays the trigger until the
   *  element is well in view — so a section near the fold does not fire on load
   *  before the visitor scrolls to it. */
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, rootMargin]);
  const hidden =
    from === 'left'
      ? '-translate-x-8 opacity-0 sm:-translate-x-16'
      : from === 'right'
        ? 'translate-x-8 opacity-0 sm:translate-x-16'
        : 'translate-y-3 opacity-0';
  return (
    <div
      ref={ref}
      className={`${className} motion-safe:transition-[opacity,translate] motion-safe:duration-700 motion-safe:ease-out ${
        shown ? 'translate-x-0 translate-y-0 opacity-100' : hidden
      }`}
      style={{ transitionDelay: shown ? `${delay}ms` : undefined }}
    >
      {children}
    </div>
  );
}
